import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { 
  Send, 
  Upload, 
  FileText, 
  Loader2, 
  CheckCircle2,
  X,
  Calendar
} from "lucide-react";
import { apiRequest, API_URL, getToken } from "../lib/utils";
import { toast } from "./ToastProvider";

/**
 * Componente de diálogo para marcar envio como realizado
 * com opção de adicionar observações e arquivo de evidência.
 */
export const MarcarEnvioDialog = ({
  open,
  onOpenChange,
  envio,
  onSuccess,
}) => {
  const [observacoes, setObservacoes] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [arquivoNome, setArquivoNome] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Apenas arquivos PDF são permitidos");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Máximo 10MB.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = getToken();
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Erro ao fazer upload');
      }

      const data = await response.json();
      setArquivo(data.url);
      setArquivoNome(file.name);
      toast.success("Arquivo enviado com sucesso!");
    } catch (error) {
      toast.error("Erro ao enviar arquivo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setArquivo(null);
    setArquivoNome("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleConfirm = async () => {
    if (!envio) return;

    setSaving(true);
    try {
      await apiRequest(`/envios/${envio.id}/marcar-enviado`, {
        method: "POST",
        body: JSON.stringify({
          observacoes: observacoes || null,
          arquivo_url: arquivo || null,
          arquivo_nome: arquivoNome || null,
        }),
      });

      toast.success("Envio marcado como realizado!");
      
      // Reset form
      setObservacoes("");
      setArquivo(null);
      setArquivoNome("");
      
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.message || "Erro ao marcar envio");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setObservacoes("");
    setArquivo(null);
    setArquivoNome("");
    onOpenChange(false);
  };

  const hoje = new Date().toLocaleDateString('pt-BR');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="marcar-envio-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-teal-100">
              <Send className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <DialogTitle className="text-lg">Marcar Envio Realizado</DialogTitle>
              <DialogDescription>
                Registre a realização do envio
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Info do envio */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="font-medium text-slate-900">{envio?.nome}</p>
            <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Dia {envio?.dia_envio}
              </span>
              <span>•</span>
              <span>Registrando em {hoje}</span>
            </div>
          </div>

          {/* Observações */}
          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações (opcional)</Label>
            <Textarea
              id="observacoes"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Informações adicionais sobre este envio..."
              rows={3}
            />
          </div>

          {/* Upload de evidência */}
          <div className="space-y-2">
            <Label>Evidência em PDF (opcional)</Label>
            <p className="text-xs text-slate-500">
              Anexe o comprovante ou protocolo do envio
            </p>
            
            {arquivo ? (
              <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700 truncate max-w-[200px]">
                    {arquivoNome}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveFile}
                  className="text-slate-500 hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="evidencia-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Selecionar PDF
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving || uploading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={saving || uploading}
            className="bg-teal-600 hover:bg-teal-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Confirmar Envio
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarcarEnvioDialog;
